import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError, money, openImpersonation } from "@/lib/api";
import { PageHeader, EmptyState, StatusPill } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Tabs, TabsList, TabsTrigger,
} from "@/components/ui/tabs";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { UsersThree, Plus } from "@phosphor-icons/react";

const emptyForm = {
  name: "", company: "", email: "", password: "", reseller_id: "__direct__",
  contact_phone: "", call_limit_price: "",
  opensips_server_ids: [], media_server_ids: [], ai_engine_ids: [],
};

export default function Customers() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const isReseller = user?.role === "reseller";
  const [rows, setRows] = useState([]);
  const [resellers, setResellers] = useState([]);
  const [opensips, setOpensips] = useState([]);
  const [media, setMedia] = useState([]);
  const [engines, setEngines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [manage, setManage] = useState(null);
  const [delTarget, setDelTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const doDelete = async () => {
    setDeleting(true);
    try {
      await api.delete(`/customers/${delTarget.id}`);
      toast.success("Customer deleted");
      setDelTarget(null); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setDeleting(false); }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      let url = "/customers";
      if (isAdmin && filter === "direct") url += "?only_direct=true";
      const reqs = [api.get(url)];
      if (isAdmin) reqs.push(api.get("/resellers"), api.get("/servers/opensips"), api.get("/servers/media"), api.get("/ai-engines"));
      else if (isReseller) reqs.push(api.get("/resellers/me/servers"));
      const results = await Promise.all(reqs);
      let data = results[0].data;
      if (isAdmin && filter === "reseller") data = data.filter((x) => x.reseller_id);
      setRows(data);
      if (isAdmin) {
        setResellers(results[1].data); setOpensips(results[2].data);
        setMedia(results[3].data); setEngines(results[4].data);
      } else if (isReseller) {
        const s = results[1].data || {};
        setOpensips(s.opensips_servers || []); setMedia(s.media_servers || []); setEngines(s.ai_engine_servers || []);
      }
    } finally {
      setLoading(false);
    }
  }, [isAdmin, isReseller, filter]);

  useEffect(() => { load(); }, [load]);

  const create = async () => {
    setSaving(true);
    try {
      const body = {
        name: form.name, company: form.company, email: form.email,
        password: form.password, contact_phone: form.contact_phone,
      };
      if (isAdmin) {
        body.reseller_id = form.reseller_id === "__direct__" ? null : form.reseller_id;
        body.pricing_source = body.reseller_id ? "reseller" : "platform";
      }
      if ((isAdmin || isReseller) && form.call_limit_price !== "") {
        body.call_limit_pricing = "custom";
        body.call_limit_price_minor = Math.max(0, Math.round(parseFloat(form.call_limit_price || 0) * 100));
      }
      if (isAdmin || isReseller) {
        body.opensips_server_ids = form.opensips_server_ids;
        body.media_server_ids = form.media_server_ids;
        body.ai_engine_ids = form.ai_engine_ids;
      }
      await api.post("/customers", body);
      toast.success("Customer created");
      setOpen(false); setForm(emptyForm); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const toggleStatus = async (c) => {
    const next = c.status === "active" ? "suspended" : "active";
    try { await api.patch(`/customers/${c.id}/status`, { status: next }); toast.success(`Customer ${next}`); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div>
      <PageHeader title="Customers" subtitle="Direct Customers (Admin) and Reseller Customers">
        {isAdmin && (
          <Tabs value={filter} onValueChange={setFilter}>
            <TabsList data-testid="customer-filter">
              <TabsTrigger value="all" data-testid="filter-all">All</TabsTrigger>
              <TabsTrigger value="direct" data-testid="filter-direct">Direct</TabsTrigger>
              <TabsTrigger value="reseller" data-testid="filter-reseller">Via Reseller</TabsTrigger>
            </TabsList>
          </Tabs>
        )}
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="create-customer-btn" className="rounded-lg">
              <Plus className="w-4 h-4 mr-1.5" weight="bold" /> New Customer
            </Button>
          </DialogTrigger>
          <DialogContent className="max-h-[85vh] overflow-y-auto" data-testid="create-customer-dialog">
            <DialogHeader><DialogTitle>Create customer</DialogTitle>
              <DialogDescription>Provisions a customer tenant, login and an isolated wallet.</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-2">
              {isAdmin && (
                <div className="space-y-2">
                  <Label>Ownership</Label>
                  <Select value={form.reseller_id} onValueChange={(v) => setForm({ ...form, reseller_id: v })}>
                    <SelectTrigger data-testid="customer-owner-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__direct__">Direct Customer (managed by Admin)</SelectItem>
                      {resellers.map((r) => <SelectItem key={r.id} value={r.id}>Reseller: {r.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2"><Label>Contact name</Label>
                  <Input data-testid="customer-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
                <div className="space-y-2"><Label>Company</Label>
                  <Input data-testid="customer-company" value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} /></div>
              </div>
              <div className="space-y-2"><Label>Email</Label>
                <Input data-testid="customer-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
              <div className="space-y-2"><Label>Password</Label>
                <Input data-testid="customer-password" type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} /></div>
              {(isAdmin || isReseller) && (
                <div className="space-y-2"><Label>Call-limit pricing <span className="text-muted-foreground text-xs font-normal">(price per concurrent channel, USD)</span></Label>
                  <Input data-testid="customer-pricing-custom" type="number" min="0" step="0.01" value={form.call_limit_price} onChange={(e) => setForm({ ...form, call_limit_price: e.target.value })} placeholder="e.g. 12.00" />
                </div>
              )}
              {(isAdmin || isReseller) && (
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>{isReseller ? "Assign Telephony" : "Assign OpenSIPS"}</Label>
                    <div className="space-y-1.5 rounded-md border border-border p-2.5 max-h-32 overflow-y-auto">
                      {opensips.map((s) => (
                        <label key={s.id} className="flex items-center gap-2 text-sm">
                          <Checkbox data-testid={`opensips-${s.id}`} checked={form.opensips_server_ids.includes(s.id)}
                            onCheckedChange={(v) => setForm((f) => ({ ...f, opensips_server_ids: v ? [...f.opensips_server_ids, s.id] : f.opensips_server_ids.filter((x) => x !== s.id) }))} />
                          {s.name} <span className="text-muted-foreground text-xs">{s.host}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Assign Media</Label>
                    <div className="space-y-1.5 rounded-md border border-border p-2.5 max-h-32 overflow-y-auto">
                      {media.map((s) => (
                        <label key={s.id} className="flex items-center gap-2 text-sm">
                          <Checkbox data-testid={`media-${s.id}`} checked={form.media_server_ids.includes(s.id)}
                            onCheckedChange={(v) => setForm((f) => ({ ...f, media_server_ids: v ? [...f.media_server_ids, s.id] : f.media_server_ids.filter((x) => x !== s.id) }))} />
                          {s.name} <span className="text-muted-foreground text-xs">{s.host}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                </div>
              )}
              {(isAdmin || isReseller) && (
                <div className="space-y-2">
                  <Label>Assign AI Engine servers <span className="text-muted-foreground text-xs font-normal">(leave empty = any healthy engine)</span></Label>
                  <div className="space-y-1.5 rounded-md border border-border p-2.5 max-h-32 overflow-y-auto" data-testid="create-aiengine-list">
                    {engines.filter((s) => s.enabled !== false && s.status !== "DISABLED").map((s) => (
                      <label key={s.id} className="flex items-center gap-2 text-sm">
                        <Checkbox data-testid={`aiengine-${s.id}`} checked={form.ai_engine_ids.includes(s.id)}
                          onCheckedChange={(v) => setForm((f) => ({ ...f, ai_engine_ids: v ? [...f.ai_engine_ids, s.id] : f.ai_engine_ids.filter((x) => x !== s.id) }))} />
                        {s.name} <span className="text-muted-foreground text-xs">{s.public_ip} · {s.status}</span>
                      </label>
                    ))}
                    {engines.filter((s) => s.enabled !== false && s.status !== "DISABLED").length === 0 && (
                      <p className="text-xs text-muted-foreground">No active AI engines available.</p>
                    )}
                  </div>
                </div>
              )}
            </div>
            <DialogFooter>
              <Button onClick={create} disabled={saving} data-testid="submit-customer-btn" className="rounded-lg">
                {saving ? "Creating…" : "Create customer"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </PageHeader>

      {loading ? <div className="text-sm text-muted-foreground">Loading…</div>
        : rows.length === 0 ? (
          <EmptyState testid="customers-empty" icon={UsersThree} title="No customers yet"
            description="Add your first customer. Admin can create Direct Customers or assign them to a reseller." />
        ) : (
          <div className="rounded-xl border border-border bg-card card-shadow overflow-hidden" data-testid="customers-table">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Company</TableHead>
                  <TableHead>Type</TableHead>
                  {isAdmin && <TableHead>Reseller</TableHead>}
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Available</TableHead>
                  <TableHead className="text-right">Call-limit</TableHead>
                  <TableHead>Signup date</TableHead>
                  <TableHead className="text-center">Campaigns</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((c) => (
                  <TableRow key={c.id} className="hover:bg-muted/40" data-testid={`customer-row-${c.id}`}>
                    <TableCell className="font-medium">{c.name}</TableCell>
                    <TableCell>
                      <span className={`text-xs px-2 py-0.5 rounded-full border capitalize ${c.customer_type === "direct" ? "bg-primary/10 text-primary border-primary/20" : "bg-muted text-muted-foreground border-border"}`}>
                        {c.customer_type || (c.reseller_id ? "reseller" : "direct")}
                      </span>
                    </TableCell>
                    {isAdmin && <TableCell className="text-sm text-muted-foreground">{c.reseller_name || "—"}</TableCell>}
                    <TableCell><StatusPill status={c.status} /></TableCell>
                    <TableCell className="text-right tabular-nums">{c.wallet ? money(c.wallet.available_minor) : "—"}</TableCell>
                    <TableCell className="text-right tabular-nums" data-testid={`customer-calllimit-${c.id}`}>{c.concurrent_call_limit != null ? c.concurrent_call_limit : "—"}</TableCell>
                    <TableCell className="text-sm text-muted-foreground whitespace-nowrap" data-testid={`customer-signup-${c.id}`}>{c.created_at ? new Date(c.created_at).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" }) : "—"}</TableCell>
                    <TableCell className="text-center tabular-nums">{c.campaign_count}</TableCell>
                    <TableCell className="text-right space-x-1">
                      <Button variant="outline" size="sm" className="rounded-lg" onClick={() => setManage(c)} data-testid={`manage-customer-${c.id}`}>Manage</Button>
                      <Button variant="ghost" size="sm" className="rounded-lg" data-testid={`login-as-customer-${c.id}`}
                        onClick={async () => { try { await openImpersonation(c.id); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } }}>
                        Login as
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => toggleStatus(c)} data-testid={`toggle-customer-${c.id}`}>
                        {c.status === "active" ? "Suspend" : "Activate"}
                      </Button>
                      <Button variant="ghost" size="sm" className="text-destructive hover:text-destructive" onClick={() => setDelTarget(c)} data-testid={`delete-customer-${c.id}`}>
                        Delete
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}

      <ManageCustomer customer={manage} isAdmin={isAdmin} isReseller={isReseller} opensips={opensips} media={media} engines={engines}
        onClose={() => setManage(null)} onSaved={load} />

      <Dialog open={!!delTarget} onOpenChange={(v) => !v && setDelTarget(null)}>
        <DialogContent data-testid="delete-customer-dialog">
          <DialogHeader><DialogTitle>Delete {delTarget?.name}?</DialogTitle>
            <DialogDescription>
              This deactivates the customer and its login. Active campaigns must be stopped first. This cannot be undone from the UI.
            </DialogDescription></DialogHeader>
          <DialogFooter>
            <Button variant="outline" className="rounded-lg" onClick={() => setDelTarget(null)} data-testid="cancel-delete-customer">Cancel</Button>
            <Button className="rounded-lg bg-destructive text-destructive-foreground hover:bg-destructive/90" onClick={doDelete} disabled={deleting} data-testid="confirm-delete-customer">
              {deleting ? "Deleting…" : "Delete customer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ManageCustomer({ customer, isAdmin, isReseller, opensips, media, engines, onClose, onSaved }) {
  const [ops, setOps] = useState([]);
  const [med, setMed] = useState([]);
  const [eng, setEng] = useState([]);
  const [busy, setBusy] = useState(false);
  const [acct, setAcct] = useState({ name: "", email: "", password: "", status: "active" });
  const [customPrice, setCustomPrice] = useState("");
  const [callLimit, setCallLimit] = useState("");

  useEffect(() => {
    if (customer) {
      setOps(customer.opensips_server_ids || []); setMed(customer.media_server_ids || []);
      setEng(customer.ai_engine_ids || []);
      setAcct({ name: customer.name || "", email: customer.contact_email || "", password: "", status: customer.status || "active" });
      setCustomPrice(customer.call_limit_price_minor != null ? (customer.call_limit_price_minor / 100).toFixed(2) : "");
      setCallLimit(customer.concurrent_call_limit != null ? String(customer.concurrent_call_limit) : "");
    }
  }, [customer]);

  if (!customer) return null;

  const saveAccount = async () => {
    if (acct.password && acct.password.length < 8) { toast.error("Password must be at least 8 characters"); return; }
    setBusy(true);
    try {
      const payload = {};
      if (acct.name && acct.name !== customer.name) payload.name = acct.name;
      if (acct.email && acct.email !== customer.contact_email) payload.email = acct.email;
      if (acct.password) payload.password = acct.password;
      if ((isAdmin || isReseller) && customPrice !== "") {
        payload.call_limit_pricing = "custom";
        payload.call_limit_price_minor = Math.max(0, Math.round(parseFloat(customPrice || 0) * 100));
      }
      if ((isAdmin || isReseller) && callLimit !== "" && String(customer.concurrent_call_limit ?? "") !== callLimit) {
        payload.concurrent_call_limit = Math.max(0, parseInt(callLimit || "0", 10));
      }
      if (Object.keys(payload).length) await api.patch(`/customers/${customer.id}`, payload);
      if (acct.status !== customer.status) await api.patch(`/customers/${customer.id}/status`, { status: acct.status });
      toast.success("Account updated");
      setAcct((a) => ({ ...a, password: "" })); onSaved();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const saveServers = async () => {
    setBusy(true);
    try {
      await api.put(`/customers/${customer.id}/servers`, { opensips_server_ids: ops, media_server_ids: med, ai_engine_ids: eng });
      toast.success("Servers assigned"); onSaved();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={!!customer} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-h-[85vh] overflow-y-auto" data-testid="manage-customer-dialog">
        <DialogHeader><DialogTitle>{customer.name}</DialogTitle>
          <DialogDescription>{customer.contact_email} · {customer.customer_type} customer</DialogDescription>
        </DialogHeader>
        <div className="space-y-5 py-2">
          <div className="space-y-3 rounded-lg border p-3" data-testid="manage-account-section">
            <Label className="text-xs uppercase tracking-wide text-muted-foreground">Account details</Label>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Contact name</Label>
                <Input data-testid="manage-acct-name" value={acct.name} onChange={(e) => setAcct({ ...acct, name: e.target.value })} /></div>
              <div className="space-y-1.5"><Label>Login email</Label>
                <Input data-testid="manage-acct-email" type="email" value={acct.email} onChange={(e) => setAcct({ ...acct, email: e.target.value })} /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Reset password</Label>
                <Input data-testid="manage-acct-password" type="password" value={acct.password} onChange={(e) => setAcct({ ...acct, password: e.target.value })} placeholder="Leave blank to keep" /></div>
              <div className="space-y-1.5"><Label>Account status</Label>
                <Select value={acct.status} onValueChange={(v) => setAcct({ ...acct, status: v })}>
                  <SelectTrigger data-testid="manage-acct-status"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="suspended">Suspended</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-1.5"><Label>Call-limit pricing <span className="text-muted-foreground text-xs font-normal normal-case">(price per concurrent channel, USD)</span></Label>
              <Input data-testid="manage-pricing-custom" type="number" min="0" step="0.01" value={customPrice} onChange={(e) => setCustomPrice(e.target.value)} placeholder="e.g. 12.00" />
              {customer.call_limit_price_minor != null && (
                <p className="text-xs text-muted-foreground">Current stored price: ${(customer.call_limit_price_minor / 100).toFixed(2)}/channel</p>
              )}
            </div>
            {(isAdmin || isReseller) && (
              <div className="space-y-1.5"><Label>Call limit <span className="text-muted-foreground text-xs font-normal normal-case">(max concurrent channels; 0 = no calls)</span></Label>
                <Input data-testid="manage-call-limit" type="number" min="0" step="1" value={callLimit} onChange={(e) => setCallLimit(e.target.value)} placeholder="e.g. 10" />
              </div>
            )}
            <Button variant="outline" onClick={saveAccount} disabled={busy} data-testid="manage-saveaccount-btn" className="rounded-lg w-full">Save account</Button>
          </div>

          {(isAdmin || isReseller) && (
            <div className="space-y-3">
              <Label>Assigned servers</Label>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5 rounded-md border p-2.5 max-h-40 overflow-y-auto">
                  <p className="text-xs uppercase tracking-wide text-muted-foreground">{isReseller ? "Telephony" : "OpenSIPS"}</p>
                  {opensips.map((s) => (
                    <label key={s.id} className="flex items-center gap-2 text-sm">
                      <Checkbox data-testid={`manage-opensips-${s.id}`} checked={ops.includes(s.id)}
                        onCheckedChange={(v) => setOps((p) => v ? [...p, s.id] : p.filter((x) => x !== s.id))} />
                      {s.name}
                    </label>
                  ))}
                </div>
                <div className="space-y-1.5 rounded-md border p-2.5 max-h-40 overflow-y-auto">
                  <p className="text-xs uppercase tracking-wide text-muted-foreground">Media</p>
                  {media.map((s) => (
                    <label key={s.id} className="flex items-center gap-2 text-sm">
                      <Checkbox data-testid={`manage-media-${s.id}`} checked={med.includes(s.id)}
                        onCheckedChange={(v) => setMed((p) => v ? [...p, s.id] : p.filter((x) => x !== s.id))} />
                      {s.name}
                    </label>
                  ))}
                </div>
              </div>
              <div className="space-y-1.5 rounded-md border p-2.5 max-h-40 overflow-y-auto" data-testid="manage-aiengine-list">
                <p className="text-xs uppercase tracking-wide text-muted-foreground">AI Engine servers <span className="normal-case">(empty = any healthy engine)</span></p>
                {engines.filter((s) => s.enabled !== false && s.status !== "DISABLED").map((s) => (
                  <label key={s.id} className="flex items-center gap-2 text-sm">
                    <Checkbox data-testid={`manage-aiengine-${s.id}`} checked={eng.includes(s.id)}
                      onCheckedChange={(v) => setEng((p) => v ? [...p, s.id] : p.filter((x) => x !== s.id))} />
                    {s.name} <span className="text-muted-foreground text-xs">{s.public_ip} · {s.status}</span>
                  </label>
                ))}
                {engines.filter((s) => s.enabled !== false && s.status !== "DISABLED").length === 0 && (
                  <p className="text-xs text-muted-foreground">No active AI engines available.</p>
                )}
              </div>
              <Button variant="outline" onClick={saveServers} disabled={busy} data-testid="manage-saveservers-btn" className="rounded-lg w-full">Save server assignment</Button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
